import { ParticleSystem as BjsParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import { Tools } from "@babylonjs/core/Misc/tools";
import { Constants } from "@babylonjs/core/Engines/constants";
import { Particle } from "@babylonjs/core/Particles/particle";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { Scene } from "@babylonjs/core/scene";
import { BoxParticleEmitter } from "@babylonjs/core/Particles/EmitterTypes/boxParticleEmitter";
import { ConeParticleEmitter } from "@babylonjs/core/Particles/EmitterTypes/coneParticleEmitter";
import { SphereParticleEmitter } from "@babylonjs/core/Particles/EmitterTypes/sphereParticleEmitter";
import { NoiseProceduralTexture } from "@babylonjs/core/Materials/Textures/Procedurals/noiseProceduralTexture";

// Bundled premultiplied default particle texture (23KB, inlined as data URL)
import defaultParticleUrl from '../assets/default-particle.png?inline';
import {
    ParticleSystemData,
    ParticleShapeType,
    Range,
    ParticleNoise,
    LEGACY_PARTICLE_FALLBACKS,
    LegacyParticleSystemData,
    LegacyMinMaxCurve,
    LegacyGradientAttributes,
    ParticleSystemCurveMode,
    LegacyGradientMode,
} from '../scenedata/behaviors/particledata';
import { LegacyParticleSystemShapeType } from '../scenedata/legacy/particledata-legacy';

import { TraceBehavior } from './tracebehavior';
import { SceneObject } from '../core/SceneObject';
import { Logger } from '../utils/Logger';
import { prewarmStepOffsetSpanningLifetime } from '../utils/ParticlePrewarm';
import { ValidationError } from '../scenedata/behaviordata';
import { SELECTION_MESH_SUFFIX } from './boxcollider';

/**
 * Particle system behavior using simplified flat data schema.
 * Supports both new format and legacy format with auto-migration.
 */
export class ParticleSystem extends TraceBehavior<ParticleSystemData> {
    static readonly behaviorType = "ParticleSystem" as const;

    private particleSystem: BjsParticleSystem | null = null;
    private particleTexture: Texture | null = null;
    private prewarmCycles: number;

    /** Track loaded texture path for reload detection */
    private _loadedTextureSrc: string = "";

    /** Procedural noise texture for turbulence */
    private noiseTexRef: NoiseProceduralTexture | null = null;
    /** Invisible mesh for edit-mode picking (particles have no geometry) */
    private _selectionMesh: Mesh | null = null;
    /** Original updateFunction before drag wrapping */
    private _defaultUpdateFn: ((particles: Particle[]) => void) | null = null;

    /** Observer for transform scale changes */
    private _scaleObserver: Observer<TransformNode> | null = null;
    /** Cached scale to detect changes */
    private _lastScale: number = 1;

    /**
     * The system a capacity-only rebuild replaced, still rendering until its
     * replacement can (see {@link _retireParticleSystem}), with the noise
     * texture it still samples.
     */
    private _outgoing: {
        system: BjsParticleSystem;
        noise: NoiseProceduralTexture | null;
        observer: Observer<Scene>;
    } | null = null;
    /** Frames a replacement gets to become ready before the outgoing system goes regardless. */
    private static readonly HANDOVER_MAX_FRAMES = 30;

    constructor(
        sceneObject: SceneObject,
        data: ParticleSystemData | LegacyParticleSystemData,
    ) {
        // Cast to ParticleSystemData - we'll migrate in constructor if needed
        super(sceneObject, data as ParticleSystemData);
        this.prewarmCycles = sceneObject.config.prewarm ?? 0;

        // Migrate legacy data immediately so it's ready before onAttach
        if (this.isLegacyData(this.getAuthored())) {
            this.migrateLegacyData(this.getAuthored() as unknown as LegacyParticleSystemData);
            this._resolved = this._resolveAll();
        }
    }

    /** Type-safe accessor for component data */
    private get data(): ParticleSystemData {
        return this._resolved;
    }

    /**
     * Validate and migrate particle system data.
     */
    protected validate(data: ParticleSystemData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Detect and migrate legacy format
        if (this.isLegacyData(data)) {
            this.migrateLegacyData(data as unknown as LegacyParticleSystemData);
        }

        // Validate required fields
        if (data.emissionRate === undefined || data.emissionRate < 0) {
            errors.push({ field: 'emissionRate', message: 'emissionRate must be >= 0' });
        }
        if (data.maxParticles === undefined || data.maxParticles < 1) {
            errors.push({ field: 'maxParticles', message: 'maxParticles must be >= 1' });
        }
        if (!data.lifetime || data.lifetime.min <= 0 || data.lifetime.max <= 0) {
            errors.push({ field: 'lifetime', message: 'lifetime min and max must be > 0' });
        }
        if (!data.shape?.type) {
            errors.push({ field: 'shape.type', message: 'shape.type is required' });
        }

        return errors;
    }

    /**
     * Check if data is in legacy format by looking for nested structure.
     * Legacy format has nested objects like emission.rateOverTime, lifetime.lifetimeAtStart.
     * New format has flat properties like emissionRate, lifetime.min/max.
     */
    private isLegacyData(data: unknown): boolean {
        const d = data as Record<string, unknown>;
        // Check for legacy nested structures
        return (d.emission as Record<string, unknown>)?.rateOverTime !== undefined ||
               (d.lifetime as Record<string, unknown>)?.lifetimeAtStart !== undefined ||
               (d.size as Record<string, unknown>)?.sizeAtStart !== undefined;
    }

    /**
     * Migrate legacy ParticleSystemData to new flat format
     */
    private migrateLegacyData(legacy: LegacyParticleSystemData): void {
        Logger.debug(`[ParticleSystem] Migrating legacy data format`);

        // Extract new values from legacy structure BEFORE deleting
        const newEmissionRate = legacy.emission?.rateOverTime ?? LEGACY_PARTICLE_FALLBACKS.emissionRate;
        const newMaxParticles = legacy.emission?.maxParticleCount ?? LEGACY_PARTICLE_FALLBACKS.maxParticles;
        const newLifetime = this.convertMinMaxToRange(legacy.lifetime?.lifetimeAtStart, LEGACY_PARTICLE_FALLBACKS.lifetime);
        const newShape = this.convertLegacyShape(legacy.shape);

        const newStartSpeed = this.convertMinMaxToRange(legacy.physicsData?.velocityAtStart, LEGACY_PARTICLE_FALLBACKS.startSpeed);
        const newGravityModifier = legacy.gravity?.gravityModifier ?? LEGACY_PARTICLE_FALLBACKS.gravityModifier;
        const newTexture = legacy.texture?.src || LEGACY_PARTICLE_FALLBACKS.texture;
        // NOTE: legacy (Unity V1) textures are raw source images, NOT premultiplied. The
        // import mode is no longer stored (particle textures are baked at authoring time;
        // see docs-internal/particle-texture-format.md), so the old material→mode breadcrumb
        // is dropped here. These legacy textures are not re-baked on load and may render
        // incorrectly under premultiplied Porter-Duff until re-imported through the editor.
        // Pre-existing limitation — tracked separately, not introduced by the field removal.
        // Warn so integrators loading unmigrated scenes get a signal rather than silent
        // incorrect rendering.
        if (legacy.texture?.src) {
            Logger.warn(
                `[ParticleSystem] Legacy particle texture "${legacy.texture.src}" is loaded as-is `
                + `(not premultiplied); it may render incorrectly until re-imported through the editor.`
            );
        }

        // Size over lifetime - convert legacy sizeAtStart to a simple constant curve
        // Transform scale is now the multiplier, so legacy size values become the curve
        let newSizeOverLifetime: Array<{ time: number; value: number }> | undefined;
        if (legacy.size?.sizeOverLifetime?.customCurve) {
            // Use existing curve if present
            newSizeOverLifetime = legacy.size.sizeOverLifetime.customCurve.map(kf => ({
                time: kf.time,
                value: kf.value
            }));
        } else if (legacy.size?.sizeAtStart) {
            // Convert legacy size to a constant size curve
            const legacySize = this.convertMinMaxToRange(legacy.size.sizeAtStart, { min: 1, max: 1 });
            // Use average of min/max as the constant size value
            const avgSize = (legacySize.min + legacySize.max) / 2;
            newSizeOverLifetime = [
                { time: 0, value: avgSize },
                { time: 1, value: avgSize }
            ];
        }

        // Color
        const colorData = legacy.color?.colorAtStart;
        const newStartColor = colorData ? this.convertLegacyColor(colorData) : LEGACY_PARTICLE_FALLBACKS.startColor;
        const newStartColor2 = (colorData?.mode === LegacyGradientMode.TwoColors && colorData.maxColor)
            ? colorData.maxColor
            : undefined;

        // Start rotation — Unity stores radians, new format stores degrees
        let newStartRotation: Range | undefined;
        if (legacy.physicsData?.rotationAtStart) {
            const rotRange = this.convertMinMaxToRange(legacy.physicsData.rotationAtStart, { min: 0, max: 0 });
            const minDeg = Tools.ToDegrees(rotRange.min);
            const maxDeg = Tools.ToDegrees(rotRange.max);
            if (minDeg !== 0 || maxDeg !== 0) {
                newStartRotation = { min: minDeg, max: maxDeg };
            }
        }

        // Angular velocity — Unity stores radians/sec, new format stores degrees/sec
        let newAngularVelocity: Range | undefined;
        if (legacy.physicsData?.angularVelocity) {
            const angularRange = this.convertMinMaxToRange(legacy.physicsData.angularVelocity, { min: 0, max: 0 });
            const minDeg = Tools.ToDegrees(angularRange.min);
            const maxDeg = Tools.ToDegrees(angularRange.max);
            if (minDeg !== 0 || maxDeg !== 0) {
                newAngularVelocity = { min: minDeg, max: maxDeg };
            }
        }

        // Noise / turbulence
        let newNoise: ParticleNoise | undefined;
        const legacyNoise = legacy.noiseData ?? (legacy as unknown as Record<string, unknown>).noiseData as LegacyParticleSystemData['noiseData'];
        if (legacyNoise?.enabled) {
            const s = this.convertMinMaxToRange(legacyNoise.strength, { min: 1, max: 1 });
            const sx = this.convertMinMaxToRange(legacyNoise.strengthX, { min: s.min, max: s.max });
            const sy = this.convertMinMaxToRange(legacyNoise.strengthY, { min: s.min, max: s.max });
            const sz = this.convertMinMaxToRange(legacyNoise.strengthZ, { min: s.min, max: s.max });
            // Unity noise directly offsets positions (no velocity accumulation).
            // We approximate this with force-based noise + high drag so that
            // velocity doesn't build up over the particle lifetime.
            const noiseScale = 2;
            newNoise = {
                strength: [sx.max * noiseScale, sy.max * noiseScale, sz.max * noiseScale],
                frequency: legacyNoise.frequency ?? 0.5,
                octaves: legacyNoise.octaves ?? 2,
                drag: 0.8,
            };

        }

        // Clean up ALL legacy properties first
        const dataAny = legacy as unknown as Record<string, unknown>;
        delete dataAny.emission;
        delete dataAny.size;
        delete dataAny.color;
        delete dataAny.physicsData;
        delete dataAny.gravity;
        delete dataAny.noiseData;
        delete dataAny.noise;
        delete dataAny.texture;
        delete dataAny.lifetime;
        delete dataAny.shape;

        // Now assign the new flat properties
        const data = legacy as unknown as ParticleSystemData;
        data["@type"] = "ParticleSystem";
        data.emissionRate = newEmissionRate;
        data.maxParticles = newMaxParticles;
        data.lifetime = newLifetime;
        data.startColor = newStartColor;
        if (newStartColor2) data.startColor2 = newStartColor2;
        data.startSpeed = newStartSpeed;
        data.gravityModifier = newGravityModifier;
        data.shape = newShape;
        data.texture = newTexture;
        if (newSizeOverLifetime) data.sizeOverLifetime = newSizeOverLifetime;
        if (newStartRotation) data.startRotation = newStartRotation;
        if (newAngularVelocity) data.angularVelocity = newAngularVelocity;
        if (newNoise) data.noise = newNoise;
    }

    private convertMinMaxToRange(minMax: LegacyMinMaxCurve | undefined, defaultVal: Range): Range {
        if (!minMax) return { ...defaultVal };

        switch (minMax.mode) {
            case ParticleSystemCurveMode.Constant:
                return { min: minMax.constant ?? 1, max: minMax.constant ?? 1 };
            case ParticleSystemCurveMode.TwoConstants:
                return { min: minMax.minValue ?? 1, max: minMax.maxValue ?? 1 };
            default:
                if (minMax.constant !== undefined) {
                    return { min: minMax.constant, max: minMax.constant };
                }
                return { ...defaultVal };
        }
    }

    private convertLegacyColor(colorData: LegacyGradientAttributes): string {
        if (colorData.mode === LegacyGradientMode.TwoColors) {
            return colorData.minColor || LEGACY_PARTICLE_FALLBACKS.startColor;
        }
        return colorData.color || LEGACY_PARTICLE_FALLBACKS.startColor;
    }

    private convertLegacyShape(shape: LegacyParticleSystemData['shape'] | undefined): ParticleSystemData['shape'] {
        if (!shape) return { ...LEGACY_PARTICLE_FALLBACKS.shape };

        switch (shape.type) {
            case LegacyParticleSystemShapeType.Sphere:
            case LegacyParticleSystemShapeType.Hemisphere:
                return { type: ParticleShapeType.Sphere, radius: shape.radius ?? 1 };
            case LegacyParticleSystemShapeType.Rectangle:
                // Rectangle is a flat box — zero out Z axis.
                // Unity Rectangle emits along local Z; set emitDirection accordingly
                // so the node rotation produces the correct world-space direction.
                return {
                    type: ParticleShapeType.Box,
                    boxSize: [shape.scale?.[0] ?? 1, shape.scale?.[1] ?? 1, 0],
                    emitDirection: [0, 0, 1],
                };
            case LegacyParticleSystemShapeType.Box:
                return {
                    type: ParticleShapeType.Box,
                    boxSize: shape.scale || [1, 1, 1]
                };
            case LegacyParticleSystemShapeType.Cone:
            case LegacyParticleSystemShapeType.ConeVolume:
            default:
                return {
                    type: ParticleShapeType.Cone,
                    radius: shape.radius ?? 0.5,
                    angle: shape.angle ?? 25
                };
        }
    }

    protected async applyCommit(_patch: Partial<ParticleSystemData>, prev: ParticleSystemData): Promise<void> {
        const textureChanged = this._resolved.texture !== this._loadedTextureSrc;
        const capacityChanged = this.particleSystem != null
            && this._resolved.maxParticles !== prev.maxParticles;

        if (textureChanged || capacityChanged) {
            if (this.attachedNode) {
                this._initAndStart(this.attachedNode, { capacityChanged, textureChanged });
            }
            return;
        }

        if (this.particleSystem) {
            this.applyAllProperties();
        }
    }

    protected onAttach(target: TransformNode): void {
        Logger.debug(`[ParticleSystem] Attaching to ${this.sceneObject.name}`);

        this._initAndStart(target);

        this.scene.onDisposeObservable.addOnce(() => {
            this.particleSystem?.dispose();
        });
    }

    /**
     * Initialize particle system, apply all properties, setup texture, start emitting.
     * Idempotent — disposes existing system first if present.
     * Called from onAttach() and hot-reload in commit().
     *
     * `capacityChanged` marks a rebuild forced by a `maxParticles` edit (Babylon's
     * capacity is fixed at construction, so changing it means a full teardown +
     * recreate). Without prewarming, the new system starts empty and ramps back up
     * via normal emission — at low target counts that empty-to-full ramp is a large
     * fraction of the visible cloud, so the edit reads as a pop/flicker. See
     * {@link _prewarmToSteadyState}.
     *
     * A capacity-only rebuild (texture path unchanged) also reuses the existing
     * `particleTexture` object instead of disposing and recreating it: Babylon's
     * `isReady()`/`render()` gate on `particleTexture.isReady()`, so a fresh
     * `Texture` — even from the same cached blob — reintroduces a blink while it
     * re-decodes, independent of the population fix above. And it hands over
     * rather than disposing first — the replaced system keeps rendering until
     * the new one is ready ({@link _retireParticleSystem}).
     */
    private _initAndStart(
        target: TransformNode,
        { capacityChanged = false, textureChanged = false }: { capacityChanged?: boolean; textureChanged?: boolean } = {},
    ): void {
        const reuseTexture = capacityChanged && !textureChanged && this.particleTexture != null;

        if (this.particleSystem) {
            if (reuseTexture) {
                this._retireParticleSystem();
            } else {
                this._disposeParticleSystem();
            }
        }

        this.initParticleSystem();
        this.applyAllProperties();

        if (reuseTexture) {
            this._reuseTexture();
        } else {
            this.setupTexture();
        }

        const prewarmed = capacityChanged && this._prewarmToSteadyState();
        this.particleSystem!.start();
        if (prewarmed) {
            this._resetPrewarmAfterStart();
        }
        this._createSelectionMesh();

        // Watch for scale changes to update particle size
        this._lastScale = this.getHierarchyScale();
        this._scaleObserver = target.onAfterWorldMatrixUpdateObservable.add(() => {
            const newScale = this.getHierarchyScale();
            if (Math.abs(newScale - this._lastScale) > 0.001) {
                this._lastScale = newScale;
                this.setupSize();
            }
        });
    }

    protected onDetach(): void {
        // Remove scale observer
        if (this._scaleObserver && this.sceneObject.transform) {
            this.sceneObject.transform.onAfterWorldMatrixUpdateObservable.remove(this._scaleObserver);
            this._scaleObserver = null;
        }

        this._disposeParticleSystem();
        Logger.debug(`[ParticleSystem] ${this.sceneObject.name} detached and cleaned up`);
    }

    // ============================================================================
    // SELECTION MESH (invisible picker for edit-mode clicking)
    // ============================================================================

    /**
     * Create an invisible mesh matching the emitter shape for edit-mode picking.
     * Particles are GPU-rendered with no geometry, so without this mesh the
     * object cannot be clicked in the viewport.
     */
    private _createSelectionMesh(): void {
        this._disposeSelectionMesh();

        const parent = this.attachedNode;
        if (!parent) return;

        const shape = this.data.shape;
        const name = `${this.sceneObject.name}${SELECTION_MESH_SUFFIX}`;
        const scene = this.sceneObject.babylonScene;

        // Minimum selection mesh size so the emitter origin is always clickable
        const MIN_SIZE = 0.3;

        switch (shape.type) {
            case ParticleShapeType.Box: {
                const boxSize = shape.boxSize ?? [1, 1, 1];
                this._selectionMesh = MeshBuilder.CreateBox(name, {
                    width: Math.max(boxSize[0], MIN_SIZE),
                    height: Math.max(boxSize[1], MIN_SIZE),
                    depth: Math.max(boxSize[2], MIN_SIZE),
                }, scene);
                break;
            }
            case ParticleShapeType.Sphere: {
                const radius = Math.max(shape.radius ?? 0.5, MIN_SIZE / 2);
                this._selectionMesh = MeshBuilder.CreateSphere(name, {
                    diameter: radius * 2,
                    segments: 8,
                }, scene);
                break;
            }
            case ParticleShapeType.Cone: {
                // Use a sphere around the cone's base radius as a simple picker
                const radius = Math.max(shape.radius ?? 0.5, MIN_SIZE / 2);
                this._selectionMesh = MeshBuilder.CreateSphere(name, {
                    diameter: radius * 2,
                    segments: 8,
                }, scene);
                break;
            }
        }

        if (this._selectionMesh) {
            this._selectionMesh.parent = parent;
            this._selectionMesh.visibility = 0;
            this._selectionMesh.isPickable = true;
        }
    }

    private _disposeSelectionMesh(): void {
        if (this._selectionMesh) {
            this._selectionMesh.dispose();
            this._selectionMesh = null;
        }
    }

    /**
     * Dispose particle system and related resources, including a replaced
     * system still handing over. Shared by onDetach() and hot-reload in commit().
     */
    private _disposeParticleSystem(): void {
        this._disposeOutgoing();
        this._releaseSystemAttachments();

        if (this.particleSystem) {
            this.particleSystem.stop();
            this.particleSystem.dispose();
            this.particleSystem = null;
        }
        if (this.particleTexture) {
            this.particleTexture.dispose();
            this.particleTexture = null;
        }
        if (this.noiseTexRef) {
            this.noiseTexRef.dispose();
            this.noiseTexRef = null;
        }
    }

    /**
     * Hand the current system over instead of disposing it: it keeps drawing
     * its live particles until its replacement is ready, then goes. It stops
     * emitting — the replacement already simulates the same emitter, and a
     * thumbnail prewarm in the handover window would otherwise grow a second
     * cloud from it. Capacity-only rebuilds only — the two share
     * `particleTexture`, so it's disposed without it.
     *
     * A new Babylon system can't render its first frame or two whatever we do:
     * `isReady()` waits on its own async import of the particle shader sources,
     * even when they're long loaded. Disposing the old system first left those
     * frames empty — measured on real WebGL, 2-3 black frames per commit, a
     * strobe on every tick of a Max Particles drag.
     */
    private _retireParticleSystem(): void {
        const current = this.particleSystem!;
        const noise = this.noiseTexRef;
        this.particleSystem = null;
        this.noiseTexRef = null;
        this._releaseSystemAttachments();
        current.stop();

        if (this._outgoing && !current.isReady()) {
            // Rebuilt again before the last replacement could render: it was
            // never on screen, so drop it and keep the visible one waiting.
            ParticleSystem._disposeSharingTexture(current, noise);
            return;
        }
        this._disposeOutgoing();
        let frames = 0;
        const observer = this.scene.onBeforeRenderObservable.add(() => {
            if (this.particleSystem?.isReady() || ++frames > ParticleSystem.HANDOVER_MAX_FRAMES) {
                this._disposeOutgoing();
            }
        });
        this._outgoing = { system: current, noise, observer };
    }

    private _disposeOutgoing(): void {
        const outgoing = this._outgoing;
        if (!outgoing) return;
        this._outgoing = null;
        this.scene.onBeforeRenderObservable.remove(outgoing.observer);
        ParticleSystem._disposeSharingTexture(outgoing.system, outgoing.noise);
    }

    /** Dispose a replaced system but not the `particleTexture` it shares with its replacement. */
    private static _disposeSharingTexture(system: BjsParticleSystem, noise: NoiseProceduralTexture | null): void {
        system.stop();
        system.dispose(false);
        noise?.dispose();
    }

    /** Release what the behavior hangs off the current system: the scale observer, the saved update function, the picking mesh. */
    private _releaseSystemAttachments(): void {
        if (this._scaleObserver && this.sceneObject.transform) {
            this.sceneObject.transform.onAfterWorldMatrixUpdateObservable.remove(this._scaleObserver);
            this._scaleObserver = null;
        }
        this._defaultUpdateFn = null;
        this._disposeSelectionMesh();
    }

    private initParticleSystem(): void {
        this.particleSystem = new BjsParticleSystem(
            `particles_${this.sceneObject.name}`,
            this.data.maxParticles,
            this.scene
        );

        this.particleSystem.preWarmCycles = this.prewarmCycles;

        // Use the scene object's transform directly as the emitter
        this.particleSystem.emitter = this.sceneObject.transform as AbstractMesh;
    }

    /**
     * Assign the kept `particleTexture` (and its blend mode) to a freshly rebuilt
     * Babylon `ParticleSystem`, instead of `setupTexture()` allocating a new
     * `Texture`. Used only when a capacity-only rebuild kept the texture —
     * see {@link _initAndStart}.
     */
    private _reuseTexture(): void {
        const ps = this.particleSystem!;
        ps.particleTexture = this.particleTexture;
        // Always use Porter-Duff blend for correct alpha compositing on transparent
        // backgrounds — the new BjsParticleSystem instance doesn't carry this over
        // from the disposed one, unlike the preserved texture object itself.
        ps.blendMode = Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
    }

    /** Full-quality prewarm step count, tuned for the reported-bug regime (see below). */
    private static readonly CAPACITY_REBUILD_PREWARM_STEPS = 120;
    /** Floor on step count so an extreme cloud still gets some fast-forwarding. */
    private static readonly CAPACITY_REBUILD_PREWARM_MIN_STEPS = 8;
    /**
     * Steady-state population the full step count is tuned for — the ticket's own
     * "most visible under ~100-110" regime. Above this, step count scales down to
     * keep total prewarm cost roughly constant (see below).
     */
    private static readonly CAPACITY_REBUILD_PREWARM_REFERENCE_POPULATION = 150;

    /**
     * Fast-forward a freshly rebuilt particle system to its steady-state population
     * before it starts, so a capacity edit doesn't visibly empty the cloud.
     *
     * `start()` runs `preWarmCycles` synchronous `animate(true)` steps (Babylon's own
     * prewarm mechanism — see thin/particleSystem's `start()`) before the first real
     * frame. We widen `preWarmStepOffset` so the burst spans roughly one particle
     * lifetime regardless of step count ({@link prewarmStepOffsetSpanningLifetime},
     * shared with `editor/ThumbnailCapture.ts`'s capture prewarm). Independent of the
     * scene-wide `config.prewarm` cycle count, which governs first-attach ramp-up,
     * not mid-edit rebuilds.
     *
     * Noise-enabled systems are skipped entirely: Babylon defers the prewarm burst
     * to an async `noiseTexture.onGeneratedObservable` + `setTimeout` callback (see
     * thinParticleSystem's `start()`), so it isn't actually synchronous there, and
     * each step also re-renders the noise render-target — real GPU cost we'd be
     * paying on every capacity edit for no synchronous benefit. They fall back to
     * the pre-existing ramp-up.
     *
     * Step count scales down as the projected steady-state population
     * (`min(capacity, emissionRate × lifetime.max)`) grows past
     * CAPACITY_REBUILD_PREWARM_REFERENCE_POPULATION: each step re-simulates every
     * currently-alive particle, so fixed-at-120 cost scales with population and
     * measured ~3.6s of main-thread blocking on a saturated 50k-particle cloud.
     * Scaling keeps total cost roughly constant instead of growing with capacity —
     * large clouds also need less help since the empty-to-full ramp is a smaller
     * fraction of a bigger cloud (the reported bug is specifically about small ones).
     * The projection is deliberately coarse: it only has to pick a cost regime, not
     * predict the count. Using `lifetime.max` rather than the mean overestimates, so
     * it errs toward fewer steps (cheaper, slightly less warm), never toward more.
     * `emissionRate` is the only way particles enter: this behavior authors no
     * bursts or `manualEmitCount`, and the Babylon system is private to it.
     *
     * Returns whether a burst was set up, i.e. whether `start()` needs
     * {@link _resetPrewarmAfterStart} afterwards.
     */
    private _prewarmToSteadyState(): boolean {
        const ps = this.particleSystem!;
        if (ps.noiseTexture) return false;

        const lifetime = Math.max(0, this.data.lifetime.max);
        const projectedPopulation = Math.min(this.data.maxParticles, this.data.emissionRate * lifetime);
        const steps = projectedPopulation > ParticleSystem.CAPACITY_REBUILD_PREWARM_REFERENCE_POPULATION
            ? Math.max(
                ParticleSystem.CAPACITY_REBUILD_PREWARM_MIN_STEPS,
                Math.round(
                    (ParticleSystem.CAPACITY_REBUILD_PREWARM_STEPS * ParticleSystem.CAPACITY_REBUILD_PREWARM_REFERENCE_POPULATION)
                    / projectedPopulation
                )
            )
            : ParticleSystem.CAPACITY_REBUILD_PREWARM_STEPS;

        ps.preWarmCycles = steps;
        ps.preWarmStepOffset = prewarmStepOffsetSpanningLifetime(steps, this.data.lifetime.max, ps.updateSpeed);
        return true;
    }

    /**
     * Restore `preWarmCycles`/`preWarmStepOffset` to their defaults after `start()`
     * has consumed the temporary burst set up by {@link _prewarmToSteadyState}.
     * Left elevated, a later `start()` call on this same instance (e.g. a play-mode
     * restart) would silently re-run the burst. This behavior never authors
     * `preWarmStepOffset` anywhere else, so Babylon's own default (`1`) is always
     * the correct restored value.
     */
    private _resetPrewarmAfterStart(): void {
        const ps = this.particleSystem!;
        ps.preWarmCycles = this.prewarmCycles;
        ps.preWarmStepOffset = 1;
    }

    /**
     * Apply all properties to the running particle system
     */
    private applyAllProperties(): void {
        if (!this.particleSystem) return;

        this.setupEmission();
        this.setupLifetime();
        this.setupSize();
        this.setupColor();
        this.setupSpeed();
        this.setupGravity();
        this.setupRotation();
        this.setupShape();
        this.setupNoise();
    }

    private setupEmission(): void {
        this.particleSystem!.emitRate = this.data.emissionRate;
    }

    private setupLifetime(): void {
        this.particleSystem!.minLifeTime = this.data.lifetime.min;
        this.particleSystem!.maxLifeTime = this.data.lifetime.max;
    }

    private setupSize(): void {
        const ps = this.particleSystem!;
        const scaleFactor = this.getHierarchyScale();
        const size = this.data.particleScale ?? 1;

        // Clear existing size gradients
        if (ps.getSizeGradients()) {
            const gradients = ps.getSizeGradients()!;
            while (gradients.length > 0) {
                ps.removeSizeGradient(gradients[0].gradient);
            }
        }

        // Size over lifetime - values are world units, scaled by transform and size
        if (this.data.sizeOverLifetime && this.data.sizeOverLifetime.length > 0) {
            for (const kf of this.data.sizeOverLifetime) {
                ps.addSizeGradient(kf.time, kf.value * size * scaleFactor);
            }
        } else {
            // Default: constant size scaled by size and transform
            ps.minSize = 1.0 * size * scaleFactor;
            ps.maxSize = 1.0 * size * scaleFactor;
        }
    }

    private setupColor(): void {
        const ps = this.particleSystem!;

        // Parse start colors from hex
        const c1 = Color4.FromHexString(this.ensureAlpha(this.data.startColor));
        const c2 = this.data.startColor2
            ? Color4.FromHexString(this.ensureAlpha(this.data.startColor2))
            : c1.clone();

        // Premultiply RGB by alpha for Porter-Duff blend.
        // Babylon writes particle vertex colors as straight RGBA, but our blend mode
        // (ALPHA_PREMULTIPLIED_PORTERDUFF) expects premultiplied values. Without this,
        // reducing alpha has no visible effect because RGB stays at full brightness.
        ps.color1 = new Color4(c1.r * c1.a, c1.g * c1.a, c1.b * c1.a, c1.a);
        ps.color2 = new Color4(c2.r * c2.a, c2.g * c2.a, c2.b * c2.a, c2.a);

        // End-of-life color. Babylon interpolates each particle from its spawn color
        // toward colorDead over its lifetime. Premultiply for Porter-Duff.
        const end = this.data.endColor
            ? Color4.FromHexString(this.ensureAlpha(this.data.endColor))
            : new Color4(0, 0, 0, 0);
        ps.colorDead = new Color4(end.r * end.a, end.g * end.a, end.b * end.a, end.a);
    }

    /**
     * Ensure hex color has alpha component
     */
    private ensureAlpha(hex: string): string {
        if (hex.length === 7) {
            return hex + "FF"; // Add full opacity
        }
        return hex;
    }

    private setupSpeed(): void {
        this.particleSystem!.minEmitPower = this.data.startSpeed.min;
        this.particleSystem!.maxEmitPower = this.data.startSpeed.max;
    }

    private setupGravity(): void {
        // Babylon uses positive Y as up, multiply by -9.8 for realistic gravity
        this.particleSystem!.gravity = new Vector3(
            0,
            this.data.gravityModifier * -9.8,
            0
        );
    }

    private setupRotation(): void {
        const ps = this.particleSystem!;

        // Start rotation (convert degrees to radians)
        if (this.data.startRotation) {
            ps.minInitialRotation = Tools.ToRadians(this.data.startRotation.min);
            ps.maxInitialRotation = Tools.ToRadians(this.data.startRotation.max);
        } else {
            ps.minInitialRotation = 0;
            ps.maxInitialRotation = 0;
        }

        // Angular velocity (convert degrees/sec to radians/sec)
        if (this.data.angularVelocity) {
            ps.minAngularSpeed = Tools.ToRadians(this.data.angularVelocity.min);
            ps.maxAngularSpeed = Tools.ToRadians(this.data.angularVelocity.max);
        } else {
            ps.minAngularSpeed = 0;
            ps.maxAngularSpeed = 0;
        }
    }

    private setupShape(): void {
        const ps = this.particleSystem!;
        const shape = this.data.shape;
        const existing = ps.particleEmitterType;

        switch (shape.type) {
            case ParticleShapeType.Sphere:
                if (existing instanceof SphereParticleEmitter) {
                    existing.radius = shape.radius ?? 1;
                } else {
                    ps.particleEmitterType = new SphereParticleEmitter(shape.radius ?? 1);
                }
                break;

            case ParticleShapeType.Cone:
                // Babylon's built-in cone emitter couples radius and angle into a single
                // cone volume (radius controls base width, angle controls slope, and spawn
                // position + direction are derived together). We override both
                // startPositionFunction and startDirectionFunction in wrapConeEmitter()
                // to decouple them: radius = spawn disc size, angle = direction spread.
                if (existing instanceof ConeParticleEmitter) {
                    existing.radius = shape.radius ?? 0.5;
                    existing.angle = Tools.ToRadians(shape.angle ?? 25);
                } else {
                    ps.particleEmitterType = new ConeParticleEmitter(
                        shape.radius ?? 0.5,
                        Tools.ToRadians(shape.angle ?? 25)
                    );
                }
                break;

            case ParticleShapeType.Box: {
                const boxSize = shape.boxSize ?? [1, 1, 1];
                const halfSize = [boxSize[0] / 2, boxSize[1] / 2, boxSize[2] / 2];
                const dir = shape.emitDirection ?? [0, 1, 0];

                if (existing instanceof BoxParticleEmitter) {
                    existing.minEmitBox.set(-halfSize[0], -halfSize[1], -halfSize[2]);
                    existing.maxEmitBox.set(halfSize[0], halfSize[1], halfSize[2]);
                    existing.direction1.set(dir[0], dir[1], dir[2]);
                    existing.direction2.set(dir[0], dir[1], dir[2]);
                } else {
                    const boxEmitter = new BoxParticleEmitter();
                    boxEmitter.minEmitBox = new Vector3(-halfSize[0], -halfSize[1], -halfSize[2]);
                    boxEmitter.maxEmitBox = new Vector3(halfSize[0], halfSize[1], halfSize[2]);
                    boxEmitter.direction1 = new Vector3(dir[0], dir[1], dir[2]);
                    boxEmitter.direction2 = new Vector3(dir[0], dir[1], dir[2]);
                    ps.particleEmitterType = boxEmitter;
                }
                break;
            }
        }

        // Wrap new emitter types to fix Babylon.js bugs.
        // Only wrap if not already wrapped (in-place updates keep the existing wrapper).
        if (ps.particleEmitterType !== existing) {
            if (ps.particleEmitterType instanceof ConeParticleEmitter) {
                // Cone emitters need both position and direction overrides:
                // - Position: emitFromSpawnPointOnly=true collapses radius to ~0
                // - Direction: default derives from position, causing double-rotation
                this.wrapConeEmitter(ps.particleEmitterType);
            } else if (!(ps.particleEmitterType instanceof BoxParticleEmitter)) {
                // Sphere: fix the double-rotation in direction.
                // Box emitters use direction1/direction2 which Babylon transforms
                // correctly via the world matrix, so no wrapping needed.
                this.wrapEmitterDirection(ps.particleEmitterType);
            }
        }
    }

    /**
     * Wrap an emitter type's startDirectionFunction to fix the double-rotation bug.
     *
     * Babylon.js cone/sphere emitters derive direction from `particle.position - emitterWorldPos`.
     * Since position is already world-transformed (rotated), this offset includes one rotation.
     * The default non-local path then applies the world matrix again — doubling rotation.
     *
     * Fix: temporarily give the emitter the local-space position so it computes a true
     * local direction, then transform that direction to world space exactly once.
     */
    private wrapEmitterDirection(emitterType: { startDirectionFunction: (worldMatrix: Matrix, directionToUpdate: Vector3, particle: Particle, isLocal: boolean, inverseWorldMatrix: Matrix) => void }): void {
        const original = emitterType.startDirectionFunction.bind(emitterType);
        const localPos = new Vector3();
        const ps = this.particleSystem!;
        emitterType.startDirectionFunction = (worldMatrix: Matrix, directionToUpdate: Vector3, particle: Particle, isLocal: boolean, inverseWorldMatrix: Matrix) => {
            // The default cone/sphere direction function computes:
            //   direction = (particle.position - worldTranslation).normalize()
            //   then TransformNormal(direction, worldMatrix) — applying rotation a second time
            //   (position was already world-transformed, so the offset already has one rotation)
            //
            // Fix: swap particle.position to localOffset + worldTranslation so the subtraction
            // yields the local offset, then the single TransformNormal gives exactly one rotation.
            const savedX = particle.position.x, savedY = particle.position.y, savedZ = particle.position.z;
            Vector3.TransformCoordinatesToRef(particle.position, ps._emitterInverseWorldMatrix, localPos);
            const tx = worldMatrix.m[12], ty = worldMatrix.m[13], tz = worldMatrix.m[14];
            particle.position.set(localPos.x + tx, localPos.y + ty, localPos.z + tz);
            // Call original (isLocal=false) — subtracts worldTranslation to get localOffset,
            // then applies worldMatrix rotation exactly once
            original(worldMatrix, directionToUpdate, particle, isLocal, inverseWorldMatrix);
            // Restore world-space position
            particle.position.set(savedX, savedY, savedZ);
        };
    }

    /**
     * Override cone emitter's position and direction functions.
     *
     * Position: emitFromSpawnPointOnly=true collapses spawn radius to ~0.
     * We spawn on a flat disc of the configured radius instead.
     *
     * Direction: the default derives direction from particle.position which
     * causes double-rotation. We compute direction directly from the cone angle,
     * picking a random direction within the cone spread and transforming once.
     */
    private wrapConeEmitter(emitterType: ConeParticleEmitter): void {
        // Position: spawn on a disc of the configured radius at emitter origin
        emitterType.startPositionFunction = (worldMatrix: Matrix, positionToUpdate: Vector3, _particle: Particle, isLocal: boolean) => {
            const a = Math.random() * Math.PI * 2;
            const r = emitterType.radius * Math.sqrt(Math.random());
            const localX = r * Math.cos(a);
            const localZ = r * Math.sin(a);
            if (isLocal) {
                positionToUpdate.set(localX, 0, localZ);
                return;
            }
            Vector3.TransformCoordinatesFromFloatsToRef(localX, 0, localZ, worldMatrix, positionToUpdate);
        };

        // Direction: pick a random direction within the cone's half-angle,
        // then transform to world space exactly once (avoiding double-rotation).
        emitterType.startDirectionFunction = (worldMatrix: Matrix, directionToUpdate: Vector3, _particle: Particle, isLocal: boolean) => {
            const halfAngle = emitterType.angle / 2;
            // Random angle around Y axis
            const phi = Math.random() * Math.PI * 2;
            // Random spread within the cone half-angle (uniform distribution on the cone cap)
            const cosHalf = Math.cos(halfAngle);
            const cosTheta = 1 - Math.random() * (1 - cosHalf);
            const sinTheta = Math.sqrt(1 - cosTheta * cosTheta);

            const localDirX = sinTheta * Math.cos(phi);
            const localDirY = cosTheta;
            const localDirZ = sinTheta * Math.sin(phi);

            if (isLocal) {
                directionToUpdate.set(localDirX, localDirY, localDirZ);
                return;
            }
            Vector3.TransformNormalFromFloatsToRef(localDirX, localDirY, localDirZ, worldMatrix, directionToUpdate);
        };
    }

    private setupNoise(): void {
        const ps = this.particleSystem!;
        const noise = this.data.noise;

        if (!noise || (noise.strength[0] === 0 && noise.strength[1] === 0 && noise.strength[2] === 0)) {
            // Disable noise and restore original updateFunction
            if (this.noiseTexRef) {
                ps.noiseTexture = null;
                this.noiseTexRef.dispose();
                this.noiseTexRef = null;
            }
            if (this._defaultUpdateFn) {
                ps.updateFunction = this._defaultUpdateFn;
            }
            return;
        }

        // Create or reuse procedural noise texture
        if (!this.noiseTexRef) {
            this.noiseTexRef = new NoiseProceduralTexture(`noise_${this.sceneObject.name}`, 256, this.scene);
        }

        this.noiseTexRef.octaves = noise.octaves ?? 2;
        // animationSpeedFactor controls how fast the noise field evolves.
        // Higher values make the force change direction more often, producing
        // flutter rather than steady drift (important since Babylon noise is
        // acceleration-based, not position-offset like Unity).
        this.noiseTexRef.animationSpeedFactor = (noise.frequency ?? 0.5) * 4;
        // brightness is an additive offset; 0.5 centers the noise around zero
        // so particles get pushed equally in both directions.
        this.noiseTexRef.brightness = 0.5;
        this.noiseTexRef.persistence = 0.7;

        ps.noiseTexture = this.noiseTexRef;
        ps.noiseStrength = new Vector3(noise.strength[0], noise.strength[1], noise.strength[2]);

        // Velocity drag: dampen forces added each frame (noise + gravity) so
        // that noise-induced velocity doesn't accumulate over a particle's
        // lifetime. This approximates Unity's position-offset noise model.
        //
        // How it works: we snapshot each particle's direction BEFORE the base
        // update (which adds gravity + noise). Afterwards we compute the delta
        // that was added this frame and scale it down by (1-drag). The base
        // emit velocity (from startSpeed, set once at spawn) is untouched.
        // Gravity survives because it's re-added every frame — drag just
        // reduces its contribution slightly, which feels like air resistance.
        if (!this._defaultUpdateFn) {
            this._defaultUpdateFn = ps.updateFunction;
        }
        const drag = noise.drag ?? 0;
        if (drag > 0) {
            const baseFn = this._defaultUpdateFn;
            // Pre-allocate snapshot arrays (will grow to match particle count)
            let snapX = new Float32Array(ps.getCapacity());
            let snapY = new Float32Array(ps.getCapacity());
            let snapZ = new Float32Array(ps.getCapacity());
            ps.updateFunction = (particles) => {
                // Grow snapshot arrays if needed
                if (particles.length > snapX.length) {
                    snapX = new Float32Array(particles.length);
                    snapY = new Float32Array(particles.length);
                    snapZ = new Float32Array(particles.length);
                }
                // Snapshot direction before gravity + noise are applied
                for (let i = 0; i < particles.length; i++) {
                    snapX[i] = particles[i].direction.x;
                    snapY[i] = particles[i].direction.y;
                    snapZ[i] = particles[i].direction.z;
                }
                baseFn(particles);
                // Frame-rate-independent exponential decay on the delta only:
                // retain = (1 - drag) ^ dt
                const retain = Math.pow(1 - drag, ps._scaledUpdateSpeed);
                for (let i = 0; i < particles.length; i++) {
                    const p = particles[i];
                    // delta = what gravity + noise added this frame
                    // dampened direction = pre-update direction + delta * retain
                    p.direction.x = snapX[i] + (p.direction.x - snapX[i]) * retain;
                    p.direction.y = snapY[i] + (p.direction.y - snapY[i]) * retain;
                    p.direction.z = snapZ[i] + (p.direction.z - snapZ[i]) * retain;
                }
            };
        } else {
            ps.updateFunction = this._defaultUpdateFn;
        }
    }

    private setupTexture(): void {
        const ps = this.particleSystem!;
        const texturePath = this.data.texture;

        this._loadedTextureSrc = texturePath;

        // Try cached blob first (covers local paths and HTTP URLs with URL mapping)
        const textureBlob = texturePath ? this.resources.getAsset(texturePath) : undefined;
        if (textureBlob) {
            const url = URL.createObjectURL(textureBlob);
            this.particleTexture = new Texture(url, this.scene, false, true, Texture.TRILINEAR_SAMPLINGMODE, () => {
                URL.revokeObjectURL(url);
            });
        } else if (texturePath) {
            // Nothing cached: a folder document's texture resolves against
            // the document's own URL (spec §2.3), as Image does.
            const url = this.resources.resolveAssetUrl(texturePath) ?? texturePath;
            this.particleTexture = new Texture(url, this.scene, false, true);
        } else {
            // Bundled premultiplied default particle texture (derived from flare.png)
            this.particleTexture = new Texture(defaultParticleUrl, this.scene);
        }

        ps.particleTexture = this.particleTexture;

        // Always use Porter-Duff blend for correct alpha compositing on transparent backgrounds.
        // On opaque black this is mathematically identical to additive (BLENDMODE_ONEONE).
        // PD is engine alpha mode 8; no BLENDMODE_ constant exists for it.
        // The PS blend mode switch falls through to setAlphaMode() for unknown values.
        ps.blendMode = Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
    }

    /**
     * Get average hierarchy scale factor for size calculations
     */
    private getHierarchyScale(): number {
        if (!this.sceneObject.transform) return 1;
        const scaling = this.sceneObject.transform.scaling;
        return (scaling.x + scaling.y + scaling.z) / 3;
    }
}
